/**
 * components/profile/PlanImpactCard.jsx — v3.7.8 Phase 84.31 Part 2
 *
 * "How this affects your plan" — a rule-driven list of personalised
 * statements returned by GET /api/profile/summary. Every statement
 * comes from real snapshot state + real user answers; nothing is
 * fabricated as filler.
 *
 * Each impact optionally carries a `review_key` referencing a life
 * profile field. When present, we render a "Review assumption →"
 * button that scrolls to the underlying question inside the tab.
 */
import React from 'react';
import { Sparkles } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

export default function PlanImpactCard({ summary, loading, error }) {
  const jumpTo = (key) => {
    if (!key) return;
    const el = document.querySelector(`[data-lp-field="${key}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      try { el.focus({ preventScroll: true }); } catch { /* non-focusable is fine */ }
    } else {
      window.scrollTo({ top: window.scrollY + 400, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <Card className="rounded-2xl border-border/70" data-testid="profile-plan-impact-loading">
        <CardContent className="p-5 space-y-3">
          <Skeleton className="h-5 w-1/3" />
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="rounded-2xl border-border/70" data-testid="profile-plan-impact-error">
        <CardContent className="p-5 text-sm text-muted-foreground">
          Couldn&rsquo;t compute your plan impact right now. Please refresh in a minute.
        </CardContent>
      </Card>
    );
  }

  const impacts = summary?.plan_impacts || [];

  return (
    <Card className="rounded-2xl border-border/70" data-testid="profile-plan-impact-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
          <h3 className="font-display text-lg font-medium text-charcoal">
            How this affects your plan
          </h3>
        </div>
        <p className="text-[12px] text-muted-foreground mt-1 leading-relaxed">
          Every statement below is generated from your actual answers and financial activity — never
          from placeholder text.
        </p>
        <ul className="mt-3 space-y-3">
          {impacts.map((i, idx) => (
            <li
              key={idx}
              className="rounded-xl border border-border/60 bg-offwhite/40 px-4 py-3"
              data-testid={`profile-plan-impact-item-${idx}`}
            >
              <div className="text-sm font-medium text-charcoal">{i.headline}</div>
              <div className="text-[13px] text-muted-foreground mt-0.5 leading-relaxed">{i.body}</div>
              {i.review_key && (
                <button
                  type="button"
                  onClick={() => jumpTo(i.review_key)}
                  className="text-primary text-[12px] hover:underline mt-1.5"
                  data-testid={`profile-plan-impact-review-${i.review_key}`}
                >
                  Review assumption →
                </button>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
